import React, {useEffect, useMemo, useState} from "react";
import {ThemeProvider} from "@mui/material/styles";
import {AuthProvider} from "react-oidc-context";
import {oidcConfig} from "./service/authService.ts";
import AppProvider from "./components/AppProvider.tsx";
import {SnackbarProvider} from "notistack";
import {muiThemeLocaleAdapter} from "./i18n/MuiThemeLocaleAdapter.ts";
import i18n from "i18next";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import "dayjs/locale/de";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {deDE} from "@mui/x-date-pickers/locales";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ColorSchemeProvider, useColorScheme} from "./theme/ColorSchemeProvider.tsx";

interface RootProps {
    children: React.ReactNode;
}

// Single client for the whole app. Default is "always fresh" and "keep nothing":
//  - staleTime 0: editable cockpit data is never served stale (refetch on mount
//    + invalidateQueries after every mutation).
//  - gcTime 0: don't retain query data after unmount. Detail forms seed their
//    fields from query data once on mount; a retained cache served
//    stale-while-revalidate data on re-open and the form kept old values until a
//    hard reload. Dropping the cache forces a fresh fetch + fresh form on re-open.
// Static read-only data (dashboards, help center) opts back into caching with
// STATIC_DATA_STALE_TIME for BOTH staleTime and gcTime (gcTime must retain the
// cache, otherwise staleTime has nothing to keep fresh).
// refetchOnWindowFocus is off so regaining focus mid-edit does not refetch a
// detail form out from under the user.
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 0,
            gcTime: 0,
            refetchOnWindowFocus: false,
        },
    },
});

// Inner provider tree that needs to read the resolved color scheme to build
// the MUI theme. Kept separate from the outer Root so ColorSchemeProvider can
// wrap everything (including AuthProvider) without forcing useColorScheme
// callers to live below ThemeProvider only.
const ThemedTree = ({children}: {children: React.ReactNode}) => {
    const {resolved} = useColorScheme();
    const [version, setVersion] = useState(0);

    // Rebuild MUI theme when the resolved scheme or i18n locale changes.
    const themeWithLocale = useMemo(() => {
        muiThemeLocaleAdapter.setMode(resolved);
        return muiThemeLocaleAdapter.buildTheme(resolved);
        // version is used to force a rebuild on languageChanged events below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolved, version]);

    useEffect(() => {
        const handleLanguageChange = () => { setVersion(v => v + 1); };
        i18n.on("languageChanged", handleLanguageChange);
        return () => { i18n.off("languageChanged", handleLanguageChange); };
    }, []);

    // Date pickers must follow the UI language on two separate channels:
    // adapterLocale drives parsing/format order (dayjs), while the empty-field
    // placeholder mask ("TT.MM.JJJJ" vs "DD.MM.YYYY") comes from localeText —
    // without it MUI falls back to English regardless of adapterLocale.
    // resolvedLanguage (not language) so region variants like "de-DE" match.
    const dateLocale = i18n.resolvedLanguage === "de" ? "de" : "en";

    return (
        <ThemeProvider theme={themeWithLocale}>
            <SnackbarProvider
                autoHideDuration={3000}
                maxSnack={3}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center"
                }}
                style={{fontFamily: "'Lato', system-ui, Arial, sans-serif"}}
                classes={{
                    containerRoot: "an-snackbar-container",
                }}
            >
                <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={dateLocale}
                    localeText={dateLocale === "de"
                        ? deDE.components.MuiLocalizationProvider.defaultProps.localeText
                        : undefined}
                >
                    {children}
                </LocalizationProvider>
            </SnackbarProvider>
        </ThemeProvider>
    );
};

const Root = (props: RootProps) => {
    return (
        <ColorSchemeProvider>
            <AuthProvider {...oidcConfig}>
                <AppProvider>
                    <QueryClientProvider client={queryClient}>
                        <ThemedTree>
                            {props.children}
                        </ThemedTree>
                    </QueryClientProvider>
                </AppProvider>
            </AuthProvider>
        </ColorSchemeProvider>
    );
};

export default Root;
